import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { UiTooltipDirective } from '../../../../../directives/ui-tooltip/ui-tooltip.directive';
import { TableColumnDefUIDirective } from '../../directives/table-column-def.directive';
import { TableOverflowObserver } from '../../services/table-overflow-observer.service';
import { TableResizeUIService } from '../../services/table-resize.service';
import { cellText, isTruncated } from '../../utils/truncation';

/** Content that shows its own tooltip on hover and tap (a stop date with its time zones). */
const OWN_TOOLTIP_SELECTOR = '[data-handles-tap]';

@Component({
  selector: 'fr8bot-ui-table-cell',
  host: { '[class.table-menu-column]': 'column?.isMenu()' },
  templateUrl: './table-cell.component.html',
  styleUrl: './table-cell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiTooltipDirective],
})
export class TableCellUIComponent {
  protected column = inject<TableColumnDefUIDirective<unknown>>(TableColumnDefUIDirective, {
    optional: true,
  });
  private resizeService = inject(TableResizeUIService, { optional: true });
  private overflowObserver = inject(TableOverflowObserver);
  private destroyRef = inject(DestroyRef);

  /** Text cut off with "…": the cell then takes focus and a tap, and shows the full text. */
  protected readonly truncated = signal(false);
  protected readonly fullText = signal('');

  private readonly cell = viewChild.required<ElementRef<HTMLElement>>('cell');
  private readonly content = viewChild.required<ElementRef<HTMLElement>>('content');

  constructor() {
    afterNextRender(() => {
      this.observeMenuContent();
      this.observeTruncation();
    });
  }

  private observeTruncation(): void {
    if (this.column?.isMenu()) {
      return;
    }

    const content = this.content().nativeElement;

    this.overflowObserver.observe(content, () => {
      const truncated = !content.querySelector(OWN_TOOLTIP_SELECTOR) && isTruncated(content);
      this.truncated.set(truncated);
      this.fullText.set(truncated ? cellText(content) : '');
    });
    this.destroyRef.onDestroy(() => this.overflowObserver.unobserve(content));
  }

  /**
   * A menu cell's content is `max-content` wide (see the SCSS), so its size is what the column
   * needs; content can change per row (a status icon next to the menu button).
   */
  private observeMenuContent(): void {
    const resizeService = this.resizeService;

    if (!resizeService || !this.column?.isMenu() || typeof ResizeObserver === 'undefined') {
      return;
    }

    const { paddingLeft, paddingRight } = getComputedStyle(this.cell().nativeElement);
    const padding = (parseFloat(paddingLeft) || 0) + (parseFloat(paddingRight) || 0);
    const content = this.content().nativeElement;
    const observer = new ResizeObserver(() =>
      resizeService.fitMenuContent(content.getBoundingClientRect().width + padding),
    );

    observer.observe(content);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }
}
